import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import Hero from './components/Hero';
import Projects from './components/Projects';
import Workflows from './components/Workflows';
import Skills from './components/Skills';
import Footer from './components/Footer';
import CommandPalette from './components/CommandPalette';
import ThemeSwitcher from './components/ThemeSwitcher';
import LanguageSwitcher from './components/LanguageSwitcher';
import { SiteProvider, scrollToSection } from './SiteContext';
import { ThemeProvider, useTheme } from './ThemeContext';
import { I18nProvider, useT } from './i18n';
import { setScrollLock } from './scrollLock';
import {
  FaLinkedin,
  FaGithub,
  FaEnvelope,
  FaPhone,
  FaBars,
  FaTimes,
  FaArrowDown,
  FaSearch,
} from 'react-icons/fa';

const NAV_ITEMS = [
  { id: 'home', key: 'nav.home' },
  { id: 'projects', key: 'nav.work' },
  { id: 'workflows', key: 'nav.automation' },
  { id: 'skills', key: 'nav.skills' },
];

function SiteShell() {
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const progressRef = useRef(null);
  const { cycleTheme } = useTheme();
  const t = useT();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;

      setIsScrolled(scrollPosition > 24);

      // Written straight to the element: this runs on every scroll frame and
      // does not need to go through React.
      if (progressRef.current) {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        const progress = scrollable > 0 ? Math.min(scrollPosition / scrollable, 1) : 0;
        progressRef.current.style.transform = `scaleX(${progress})`;
      }

      const offset = 140;
      for (const { id } of NAV_ITEMS) {
        const element = document.getElementById(id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop - offset && scrollPosition < offsetTop + offsetHeight - offset) {
            setActiveSection(id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // One observer drives every scroll reveal on the site. Components opt in by
  // adding the `reveal` class; nothing else needs its own observer.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const observeAll = () => {
      document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
    };

    observeAll();
    // Filtered project grids swap their children in without a remount, so pick
    // up anything that appears after the first pass.
    const mutation = new MutationObserver(observeAll);
    mutation.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutation.disconnect();
    };
  }, []);

  // ⌘K / Ctrl+K opens the palette from anywhere; Shift+T cycles the theme.
  useEffect(() => {
    const isTyping = (target) =>
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable);

    const handleKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }

      // Plain Shift+T only: no modifier combinations, never while typing, and
      // not while the palette owns the keyboard.
      if (
        e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        e.key.toLowerCase() === 't' &&
        !isTyping(e.target)
      ) {
        // Read the open state through the setter so the listener does not have
        // to be re-bound on every open and close.
        setPaletteOpen((open) => {
          if (!open) cycleTheme();
          return open;
        });
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [cycleTheme]);

  useEffect(() => {
    setScrollLock('menu', mobileMenuOpen);
    return () => setScrollLock('menu', false);
  }, [mobileMenuOpen]);

  useEffect(() => {
    setScrollLock('palette', paletteOpen);
    return () => setScrollLock('palette', false);
  }, [paletteOpen]);

  const goToSection = (sectionId) => {
    scrollToSection(sectionId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="App">
      <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t(mobileMenuOpen ? 'a11y.closeMenu' : 'a11y.openMenu')}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>

            <a
              className="wordmark"
              href="#home"
              onClick={(e) => {
                e.preventDefault();
                goToSection('home');
              }}
            >
              <span className="wordmark-mark" aria-hidden="true">HT</span>
              <span className="wordmark-name">Herve Tusse</span>
            </a>

            <nav className={`navigation ${mobileMenuOpen ? 'mobile-open' : ''}`}>
              {NAV_ITEMS.map(({ id, key }) => (
                <button
                  key={id}
                  className={`nav-btn ${activeSection === id ? 'active' : ''}`}
                  onClick={() => goToSection(id)}
                >
                  {t(key)}
                </button>
              ))}
            </nav>

            <div className="contact-info">
              <button
                className="header-search"
                onClick={() => setPaletteOpen(true)}
                aria-label={t('a11y.searchCommands')}
              >
                <FaSearch />
                <span className="header-search-text">{t('header.search')}</span>
                <kbd className="header-search-kbd">⌘K</kbd>
              </button>

              <LanguageSwitcher />
              <ThemeSwitcher />

              <a
                href={t('resume.file')}
                className="btn btn-ghost header-cv"
                download
              >
                <FaArrowDown />
                {t('header.cv')}
              </a>
              <div className="contact-links">
                <a href="mailto:hervetusse@gmail.com" className="contact-link" title={t('contact.email')} aria-label={t('a11y.email')}>
                  <FaEnvelope />
                </a>
                <a href="tel:+27641704892" className="contact-link" title={t('contact.phone')} aria-label={t('a11y.phone')}>
                  <FaPhone />
                </a>
                <a href="https://www.linkedin.com/in/herve-tusse-8a2479177/" className="contact-link" title="LinkedIn" target="_blank" rel="noopener noreferrer" aria-label={t('a11y.linkedin')}>
                  <FaLinkedin />
                </a>
                <a href="https://github.com/htusse" className="contact-link" title="GitHub" target="_blank" rel="noopener noreferrer" aria-label={t('a11y.github')}>
                  <FaGithub />
                </a>
              </div>
            </div>
          </div>
        </div>

        <span className="scroll-progress" ref={progressRef} aria-hidden="true" />
      </header>

      <div
        className={`nav-scrim ${mobileMenuOpen ? 'is-open' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <main className="main-content">
        <section id="home">
          <Hero />
        </section>
        <Projects />
        <Workflows />
        <Skills />
      </main>

      <Footer />

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

function App() {
  // I18nProvider outermost: language is upstream of appearance and filtering,
  // and neither of those reads it.
  return (
    <I18nProvider>
      <ThemeProvider>
        <SiteProvider>
          <SiteShell />
        </SiteProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
